import type { MaybeRefOrGetter } from 'vue';
import type { Filter, ResolvedMonospaceApiKey } from '#layers/data-model/public';
import { computed, onScopeDispose, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '#imports';
import { isPastDateTime } from '#layers/base/public';
import { isRelationalFilter, usePaginatedQuery } from '#layers/data-model/public';
import { DEFAULT_LISTING_PAGE_SIZE, showConfirmationDialog, usePagination, useToastResultHandling } from '#layers/ui/public';

const API_KEY_COLLECTION = 'MonospaceApiKey';

interface UseApiKeysProps {
	// If no filter is provided, no query will be executed
	// But we can still create/deactivate/reactivate/revoke api keys
	filter?: MaybeRefOrGetter<Filter>;
}

export function useApiKeys(props?: MaybeRefOrGetter<UseApiKeysProps>) {
	const { t } = useI18n();
	const store = useStore();

	const filter = computed(() => toValue(props)?.filter ?? {});
	const hasFilter = computed(() => Boolean(toValue(props)?.filter));
	const fetchPolicy = computed(() => isRelationalFilter(filter.value) ? 'no-cache' : 'cache-first');

	// TODO: Refactor useItems to use base composable that works independent of Resolved schema
	// https://linear.app/directus/issue/NEXT-1929/refactor-useitems-to-use-base-composable-that-works-independent-of

	// Lightweight id-only query to produce a stable total count
	// This will only work to the max limit configured in the engine which is 100.
	// TODO: Remove this once aggregate count is ready in the engine
	const idsQuery = store.MonospaceApiKey.query((q) => q.many({
		filter: filter.value,
		enabled: hasFilter.value,
		fields: ['id'],
		fetchPolicy: fetchPolicy.value,
		params: { limit: -1 },
	}));

	// Workaround until aggregate count is ready in the engine
	const totalItemsCount = computed(() => idsQuery.data.value?.length ?? 0);

	const { pagination, setPage, setPageSize } = usePagination(
		{ page: 1, pageSize: DEFAULT_LISTING_PAGE_SIZE },
		totalItemsCount,
	);

	const pageNumber = computed({
		get: () => pagination.value.page,
		set: setPage,
	});

	const pageSize = computed({
		get: () => pagination.value.pageSize,
		set: setPageSize,
	});

	const collectionQuery = store.MonospaceApiKey.query((q) => q.many({
		filter: filter.value,
		enabled: hasFilter.value,
		include: { user: true, creator: true },
		fetchPolicy: fetchPolicy.value,
		sort: [{ active: { direction: 'asc' } }, { expiresAt: { direction: 'asc' } }],
		pageIndex: 0,
		pageSize: pagination.value.pageSize,
	}));

	const { items: apiKeys, allItems: allApiKeys } = usePaginatedQuery<ResolvedMonospaceApiKey>({
		collectionQuery,
		pageNumber,
		collectionApiName: API_KEY_COLLECTION,
	});

	function refreshIdsOnMutation({ collection, mutation }: { collection: { name: string }; mutation: 'create' | 'update' | 'delete' }) {
		if (collection.name !== API_KEY_COLLECTION) return;
		if (mutation !== 'create' && mutation !== 'delete') return;
		if (idsQuery.loading.value) return;
		idsQuery.refresh();
	}

	const offAfterMutation = store.$hooks.hook('afterMutation', refreshIdsOnMutation);
	const offAfterManyMutation = store.$hooks.hook('afterManyMutation', refreshIdsOnMutation);

	onScopeDispose(() => {
		offAfterMutation();
		offAfterManyMutation();
	});

	const isLoading = computed(() => collectionQuery.loading.value || idsQuery.loading.value);

	const { onSuccess: deactivateSuccess, onError: deactivateError } = useToastResultHandling({
		success: {
			title: t('api_keys.deactivate_success'),
		},
		error: {
			title: t('api_keys.deactivate_error'),
		},
	});

	const { onSuccess: activateSuccess, onError: activateError } = useToastResultHandling({
		success: {
			title: t('api_keys.reactivate_success'),
		},
		error: {
			title: t('api_keys.reactivate_error'),
		},
	});

	const { onSuccess: revokeSuccess, onError: revokeError } = useToastResultHandling({
		success: {
			title: t('api_keys.revoke_success'),
		},
		error: {
			title: t('api_keys.revoke_error'),
		},
	});

	async function deactivate(apiKey: ResolvedMonospaceApiKey) {
		try {
			await store.MonospaceApiKey.update({ id: apiKey.id, active: false });
			deactivateSuccess();
		}
		catch (error) {
			deactivateError(error);
		}
	}

	async function reactivate(apiKey: ResolvedMonospaceApiKey) {
		try {
			await store.MonospaceApiKey.update({ id: apiKey.id, active: true });
			activateSuccess();
		}
		catch (error) {
			activateError(error);
		}
	}

	async function revoke(apiKey: ResolvedMonospaceApiKey) {
		const confirmed = await showConfirmationDialog({
			props: {
				title: t('api_keys.revoke_confirm.title'),
				description: t('api_keys.revoke_confirm.description'),
				confirm: {
					color: 'error',
					label: `${t('api_keys.revoke')}`,
				},
			},
		});

		if (!confirmed) return;

		try {
			await store.MonospaceApiKey.delete(apiKey.id);
			revokeSuccess();
		}
		catch (error) {
			revokeError(error);
		}
	}

	function refresh() {
		idsQuery.refresh();
		collectionQuery.refresh();
	}

	return {
		apiKeys,
		allApiKeys,
		pageNumber,
		pageSize,
		totalItemsCount,
		isLoading,
		deactivate,
		reactivate,
		refresh,
		revoke,
		isExpired,
		isDisabled,
	};
}

function isExpired(apiKey: ResolvedMonospaceApiKey): boolean {
	if (!apiKey.expiresAt) return false;
	return isPastDateTime(apiKey.expiresAt);
}

function isDisabled(apiKey: ResolvedMonospaceApiKey): boolean {
	return !apiKey.active || isExpired(apiKey);
}
